import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
  type SyntheticEvent,
} from 'react';
import {
  useAutomationsDispatch,
  useAutomationsSelector,
} from 'pages/AutomationEngine/store/react';
import { actions } from 'pages/AutomationEngine/store';
import { format } from 'date-fns';
import {
  HistorySideBar,
  ButtonWrapper,
  HistoryWrapperPage,
  ButtonCard,
  NumberWrapper,
  HistoryButton,
  TopGradient,
  BottomGradient,
  LoadingWrapper,
  StyledErrorLink,
} from './styles';
import { HistoryButtons } from './HistoryButtons';
import { errorByStatus } from '../ts-helpers';
import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import { Button as ButtonKDS } from '@kizen/kds/Button';
import type {
  ExecutionHistory,
  HistoryDetailedLog,
} from 'pages/AutomationEngine/types';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';
import { useVerticalScroll } from '../hooks/useVerticalScroll';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { useSelector } from 'react-redux';
import { getBusinessTimeZone } from 'store/authentication/selectors';
import {
  useHistoryNavigation,
  type HistoryWithStep,
} from './useHistoryNavigation';
import { getHistoryVariableValue, historyTitle } from './helpers';
import { ValueWithTooltip } from './ValueWithTooltip';
import { IconWithTooltip } from 'pages/Common/components/LinkWithIconTooltip/IconWithTooltip';
import type { AutomationStep } from 'ts-components/Plugins/types';
import {
  getStatusDecoration,
  getHistoryStatusColor,
  getHistoryStatusLabel,
} from 'pages/AutomationEngine/Engine/Steps/ConfiguredStepCard/History/helpers';

export const HistoryVariableValue = ({
  detailedLog,
}: {
  detailedLog: HistoryDetailedLog;
}) => {
  const { t } = useTranslation();
  const businessTimezone = useSelector(getBusinessTimeZone);
  const variableValue = getHistoryVariableValue(
    detailedLog,
    businessTimezone,
    t
  );

  if (typeof variableValue !== 'string' && variableValue?.tooltip) {
    const { text, tooltip } = variableValue;
    const variableText = `${detailedLog.variable_name} = ${text}`;
    const variableTooltip = `${variableText}\n\n${tooltip}`;
    return <ValueWithTooltip text={variableText} tooltip={variableTooltip} />;
  }

  return detailedLog?.variable_name ? (
    <TextEllipsisWithTooltip>
      {`${detailedLog.variable_name} = ${variableValue}`}
    </TextEllipsisWithTooltip>
  ) : null;
};

export const HistoryFrameWork = ({
  children,
  navHeight,
}: {
  children: ReactNode | ReactNode[];
  navHeight: number;
}) => {
  const { t } = useTranslation();
  const dispatch = useAutomationsDispatch();
  const automationSteps: AutomationStep<{ name: string }>[] =
    useAutomationsSelector((state: any) => state.automationPlugins);
  const { containerProps, topFade, bottomFade } = useVerticalScroll([
    children,
    navHeight,
  ]);

  const {
    historyId,
    showHistorySideBar,
    selectedHistoryStep,
    isHistoryFetching,
    executionMode,
  } = useContext(ExecutionHistoryContext);

  const { handleClick, clickUp, clickDown, orderedExecutionHistoryWithStep } =
    useHistoryNavigation();

  const shortcuts = useMemo(
    () => [
      { key: 'ArrowUp', action: clickUp },
      { key: 'ArrowDown', action: clickDown },
    ],
    [clickUp, clickDown]
  );

  useKeyboardShortcut(shortcuts, Boolean(showHistorySideBar && historyId));

  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (selectedHistoryStep && refs.current[selectedHistoryStep.id]) {
      refs.current[selectedHistoryStep.id]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedHistoryStep]);

  const handleShowError = useCallback(
    (event: SyntheticEvent, history: ExecutionHistory) => {
      event.preventDefault();
      dispatch(actions.showHistoryErrorModal([history]));
    },
    [dispatch]
  );

  if (showHistorySideBar && historyId) {
    return (
      <HistoryWrapperPage navHeight={navHeight}>
        <HistorySideBar>
          <HistoryButtons>
            <HistoryButton
              variant="text"
              size="small"
              onClick={clickUp}
              rightIcon="custom-chevron-up"
              disabled={
                selectedHistoryStep?.id ===
                orderedExecutionHistoryWithStep[0]?.id
              }
              color="secondary"
            >
              {t('Previous')}
            </HistoryButton>
            <HistoryButton
              variant="text"
              size="small"
              onClick={clickDown}
              rightIcon="custom-chevron-down"
              disabled={
                selectedHistoryStep?.id ===
                  orderedExecutionHistoryWithStep.at(-1)?.id ||
                orderedExecutionHistoryWithStep.length === 0
              }
              color="secondary"
            >
              {t('Next')}
            </HistoryButton>
          </HistoryButtons>
          <ButtonWrapper
            executionMode={executionMode}
            className="kds-expanding-scrollbar"
            data-qa="history-scroll-wrapper"
            {...containerProps}
          >
            <TopGradient position="top" fadeIn={topFade} />

            {!isHistoryFetching ? (
              orderedExecutionHistoryWithStep.map(
                (history: HistoryWithStep, index: number) => {
                  const hasError = errorByStatus(history.status);

                  const adjustedIndex = index + 1; // Adjust index to start from 1

                  const { created, updated, detailedLog } = history;

                  const statusColor = `var(--color-${getHistoryStatusColor(
                    history.status
                  )})`;

                  const statusLabel = getHistoryStatusLabel(history.status, t);

                  const stepOutput = getStatusDecoration(
                    history,
                    history!.step,
                    t
                  );

                  const title = historyTitle(history, automationSteps, t);
                  return (
                    <ButtonCard
                      key={history.id}
                      onClick={() => handleClick(index)}
                      color="var(--color-font-white)"
                      hoverColor="var(--color-background-light)"
                      disabled={selectedHistoryStep?.id === history.id}
                      ref={(el) => (refs.current[history.id] = el)}
                      data-qa={`history-card-${history.id}`}
                      data-qa-selected={selectedHistoryStep?.id === history.id}
                    >
                      <div className="circle">
                        <NumberWrapper
                          color={
                            hasError
                              ? `var(--color-${getHistoryStatusColor('failed')})`
                              : 'var(--color-background-medium)'
                          }
                        >
                          {adjustedIndex}
                        </NumberWrapper>
                      </div>
                      <div className="text">
                        <div className="status">
                          <KizenTypography as="div" size="button" weight="bold">
                            {title}
                            {history.step?.deleted ? (
                              <>
                                {' '}
                                <IconWithTooltip
                                  label={history.step.description}
                                />
                              </>
                            ) : null}
                          </KizenTypography>
                          {hasError ? (
                            <ButtonKDS
                              variant="text"
                              rightIcon="message-warning-triangle"
                              rightIconSettings={{
                                tooltipDescriptionOverride: t('Failed'),
                              }}
                              color="warning"
                              size="small"
                              onClick={(event) =>
                                handleShowError(event, history)
                              }
                            />
                          ) : null}
                        </div>
                        <KizenTypography as="div" size="button">
                          {t('Queued at {{queued}}', {
                            queued: format(created, 'MM/dd/yyyy HH:mm:ss'),
                          })}
                        </KizenTypography>
                        <KizenTypography as="div" size="button">
                          {t(`Run at {{run}} in {{time}} ms`, {
                            run: format(updated, 'MM/dd/yyyy HH:mm:ss'),
                            time:
                              new Intl.NumberFormat().format(
                                history.executionTimeMs
                              ) || 0,
                          })}
                        </KizenTypography>
                        <div>
                          <KizenTypography as="span" size="button">
                            {t('Status: ')}
                          </KizenTypography>
                          {hasError ? (
                            <StyledErrorLink
                              as="span"
                              size="button"
                              weight="bold"
                              type="link"
                              onClick={(event) =>
                                handleShowError(event, history)
                              }
                            >
                              {statusLabel}
                              {stepOutput}
                            </StyledErrorLink>
                          ) : history.detailedLog ? (
                            <KizenTypography
                              as="span"
                              size="button"
                              weight="bold"
                              type="link"
                              color={statusColor}
                              onClick={(event) =>
                                handleShowError(event, history)
                              }
                            >
                              {statusLabel}
                              {stepOutput}
                            </KizenTypography>
                          ) : (
                            <KizenTypography
                              as="span"
                              size="button"
                              weight="bold"
                              color={statusColor}
                            >
                              {statusLabel}
                              {stepOutput}
                            </KizenTypography>
                          )}
                        </div>
                        {!hasError && detailedLog ? (
                          <HistoryVariableValue detailedLog={detailedLog} />
                        ) : null}
                      </div>
                    </ButtonCard>
                  );
                }
              )
            ) : (
              <LoadingWrapper>
                <KizenTypography
                  className="info"
                  as="div"
                  size="text"
                  color="var(--color-font-secondary)"
                >
                  {t('Loading...')}
                </KizenTypography>
              </LoadingWrapper>
            )}
          </ButtonWrapper>
          <BottomGradient position="bottom" fadeIn={bottomFade} />
        </HistorySideBar>
        {children}
      </HistoryWrapperPage>
    );
  }

  return <>{children}</>;
};
